import React from 'react';
import Tilt from 'react-parallax-tilt';
import styled, { css } from 'styled-components';

import {
  promoTextColor,
  promoGradientEnd,
  promoGradientStart,
  promoGradientDegrees,
} from 'common/components/settings/referrals/process';
import Mark from 'components/brand/Mark';
import FlexContainer from 'components/lib/ui/FlexContainer';
import Text from 'components/lib/ui/Text';
import { TicketSVG } from 'components/settings/referrals/TicketSVG';

import { fontSize, radius, spacing, variables } from 'common/lib/theme/dynamic';
import useTheme from 'lib/hooks/useTheme';

const StyledTilt = styled(Tilt)`
  width: 100%;
`;

const Sizer = styled.div`
  aspect-ratio: 1.67/1;
  width: 100%;
`;

const TextSizer = styled(Sizer)`
  position: relative;
`;

// Filter used so that the SVG cutouts get a nice looking shadow
const shadow = css`
  filter: drop-shadow(0px 3px 6px ${variables.color.shadow.large});
`;

const GiftCard = styled(TextSizer)`
  position: absolute;
  top: 0;
  border-radius: ${radius.medium};
  background: linear-gradient(
    ${promoGradientDegrees}deg,
    ${({ theme }) => promoGradientStart({ theme })} -9.43%,
    ${({ theme }) => promoGradientEnd({ theme })} 90.78%
  );
  ${shadow}
`;

const StyledTicketSVG = styled(TicketSVG)`
  position: absolute;
  top: 0;
  ${shadow}
`;

const MarkContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const StyledMark = styled(Mark)<{ scale: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: rotate(-10deg) scale(${({ scale }) => scale});
  opacity: ${({ theme }) => (theme.uiTheme === 'dark' ? 0.02 : 0.08)};
`;

const Content = styled(FlexContainer).attrs({ column: true, justifyCenter: true })`
  height: 100%;
  text-align: center;
  padding: ${spacing.default};
  position: relative;
`;

const Title = styled(Text).attrs({
  weight: 'medium',
})<{ isSidebarExpanded?: boolean }>`
  text-align: center;
  font-size: clamp(${fontSize.xlarge}, 2.5vw, ${fontSize.xxlarge});
  color: ${promoTextColor};

  ${({ isSidebarExpanded, theme }) =>
    isSidebarExpanded
      ? css`
          @media (max-width: ${theme.breakPoints.xl}px) {
            font-size: clamp(${fontSize.base}, 2.2vw, ${fontSize.xxlarge});
          }
          @media (max-width: ${theme.breakPoints.lg}px) {
            font-size: clamp(${fontSize.xlarge}, 5vw, ${fontSize.xxlarge});
          }
        `
      : css`
          @media (max-width: ${theme.breakPoints.md}px) {
            font-size: clamp(${fontSize.large}, 3vw, ${fontSize.xxlarge});
          }
          @media (max-width: ${theme.breakPoints.sm}px) {
            font-size: clamp(${fontSize.xlarge}, 5vw, ${fontSize.xxlarge});
          }
        `}
`;

const Description = styled(Text).attrs({
  size: 'xxsmall',
  weight: 'medium',
})`
  color: ${promoTextColor};
  text-align: center;
  opacity: 0.8;
  margin-top: -${spacing.xxxsmall};
`;

type Props = {
  className?: string;
  isSidebarExpanded?: boolean;
  title: string;
  description: string;
  variant: 'ticket' | 'giftCard';
};

export const Promo = ({ className, isSidebarExpanded, title, description, variant }: Props) => {
  const theme = useTheme();

  return (
    <StyledTilt className={className} tiltMaxAngleX={8} tiltMaxAngleY={8}>
      {variant === 'giftCard' ? <GiftCard /> : <StyledTicketSVG />}

      <Sizer>
        <MarkContainer>
          <StyledMark
            color={theme.variables.color.content.white}
            scale={variant === 'giftCard' ? 1.4 : 1.26}
          />
        </MarkContainer>

        <Content>
          <Title isSidebarExpanded={isSidebarExpanded}>{title}</Title>
          <Description>{description}</Description>
        </Content>
      </Sizer>
    </StyledTilt>
  );
};
